import type { Asset } from "schemas/generated/asset";

import * as React from "react";

import SolidColorPicker from "@editor/components/common/designSystem/color/SolidColorPicker";
import useCurrentDragType from "@editor/hooks/useCurrentDragType";
import { trpc } from "@editor/utils/trpc";

import AssetsLibrary from "@/features/assets/components/AssetsLibrary";
import Button from "@replo/design-system/components/button/Button";
import twMerge from "@replo/design-system/utils/twMerge";
import { XIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { ASSETS_HOST } from "replo-utils/lib/assets";

type FormValues = {
  selectedAssetIds: string[];
};

export interface OptionImageSetFormRef {
  submit: () => void;
  isDirty: boolean;
}

interface ProductsOptionImageSetFormProps {
  optionValueId: string;
  optionName: string;
  optionValue: string;
  currentAssetIds: string[];
  currentColor: string | null;
  currentImage: string | null;
  mode: "imageList" | "thumbnail";
  onSubmitSuccess: () => void;
  onCancel: () => void;
  projectId: string;
  productId: string;
}

export const ProductsOptionImageSetForm = React.forwardRef<
  OptionImageSetFormRef,
  ProductsOptionImageSetFormProps
>(
  (
    {
      optionValueId,
      currentAssetIds,
      currentColor,
      mode,
      onSubmitSuccess,
      productId,
      projectId,
    },
    ref,
  ) => {
    const {
      handleSubmit,
      setValue,
      watch,
      formState: { isDirty },
    } = useForm<FormValues>({
      defaultValues: { selectedAssetIds: [] },
    });

    const selectedAssetIds = watch("selectedAssetIds");
    const [selectedHexColor, setSelectedHexColor] = React.useState<
      string | null
    >(currentColor);
    const [hasInitialized, setHasInitialized] = React.useState(false);
    const [draggedOverIndex, setDraggedOverIndex] = React.useState<
      number | null
    >(null);

    const utils = trpc.useUtils();
    const { setCurrentDragTypeAndIdentifier } = useCurrentDragType();

    const isImageListMode = mode === "imageList";
    const isThumbnailMode = mode === "thumbnail";

    React.useImperativeHandle(ref, () => ({
      submit: handleSubmit(onSubmit),
      isDirty,
    }));

    React.useEffect(() => {
      if (hasInitialized) {
        return;
      }

      if (isImageListMode) {
        setValue("selectedAssetIds", currentAssetIds, { shouldDirty: false });
        setSelectedHexColor(null);
      } else if (isThumbnailMode) {
        const initialAssetIds =
          currentAssetIds.length > 0 ? currentAssetIds.slice(0, 1) : [];
        setValue("selectedAssetIds", initialAssetIds, { shouldDirty: false });
        setSelectedHexColor(currentColor);
      }

      setHasInitialized(true);
    }, [
      isImageListMode,
      isThumbnailMode,
      currentAssetIds,
      currentColor,
      setValue,
      hasInitialized,
    ]);

    const onMutationSuccess = async () => {
      await utils.product.get.invalidate({ type: "id", id: productId });
      onSubmitSuccess();
    };

    const updateMediaMutation = trpc.product.updateOptionValueMedia.useMutation(
      {
        onSuccess: onMutationSuccess,
      },
    );
    const updateColorMutation = trpc.product.updateOptionValueColor.useMutation(
      { onSuccess: onMutationSuccess },
    );

    const handleAssetStaged = (asset: Asset) => {
      if (isThumbnailMode) {
        setValue("selectedAssetIds", [asset.id], {
          shouldValidate: true,
          shouldDirty: true,
        });
        setSelectedHexColor(null);
      } else {
        const isAlreadySelected = selectedAssetIds.some(
          (id) => id === asset.id,
        );
        if (!isAlreadySelected) {
          setValue("selectedAssetIds", [...selectedAssetIds, asset.id], {
            shouldValidate: true,
            shouldDirty: true,
          });
        }
      }
    };

    const handleRemoveStagedAsset = (assetIdToRemove: string) => {
      if (isThumbnailMode) {
        setValue("selectedAssetIds", [], {
          shouldValidate: true,
          shouldDirty: true,
        });
        setSelectedHexColor(null);
      } else {
        setValue(
          "selectedAssetIds",
          selectedAssetIds.filter((id) => id !== assetIdToRemove),
          { shouldValidate: true, shouldDirty: true },
        );
      }
    };

    const handleSolidColorPickerChange = (color: string | null) => {
      setSelectedHexColor(color);
      if (color) {
        setValue("selectedAssetIds", [], {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    };

    const onSubmit = (data: FormValues) => {
      if (isThumbnailMode) {
        const isColorChanged = selectedHexColor !== currentColor;
        const hasSelectedAsset = data.selectedAssetIds.length > 0;
        const currentHasAsset = currentAssetIds.length > 0;
        const isImageChanged =
          hasSelectedAsset !== currentHasAsset ||
          (hasSelectedAsset && data.selectedAssetIds[0] !== currentAssetIds[0]);

        if (isColorChanged) {
          updateColorMutation.mutate({
            optionValueId,
            color: selectedHexColor,
          });
        }
        if (isImageChanged) {
          updateMediaMutation.mutate({
            optionValueId,
            media: data.selectedAssetIds.map((id, index) => ({
              assetId: id,
              sortOrder: index,
            })),
          });
        }
        if (!isColorChanged && !isImageChanged) {
          onSubmitSuccess();
        }
      } else {
        updateMediaMutation.mutate({
          optionValueId,
          media: data.selectedAssetIds.map((id, index) => ({
            assetId: id,
            sortOrder: index,
          })),
        });
      }
    };

    const handleDragStart = (
      e: React.DragEvent<HTMLDivElement>,
      assetId: string,
    ) => {
      setCurrentDragTypeAndIdentifier("stagedAssetReorder", assetId);
      e.dataTransfer.setData("text/plain", assetId);
      e.dataTransfer.effectAllowed = "move";
    };

    const handleDragEnd = () => setDraggedOverIndex(null);

    const handleDragOver = (
      e: React.DragEvent<HTMLDivElement>,
      index: number,
    ) => {
      e.preventDefault();
      if (index !== draggedOverIndex) {
        setDraggedOverIndex(index);
      }
      e.dataTransfer.dropEffect = "move";
    };

    const handleDragLeave = () => setDraggedOverIndex(null);

    const handleDrop = (
      e: React.DragEvent<HTMLDivElement>,
      targetIndex: number,
    ) => {
      e.preventDefault();

      const draggedAssetId = e.dataTransfer.getData("text/plain");
      setCurrentDragTypeAndIdentifier(null, null);
      setDraggedOverIndex(null);

      if (!draggedAssetId) {
        return;
      }

      const draggedItemIndex = selectedAssetIds.findIndex(
        (id) => id === draggedAssetId,
      );
      if (draggedItemIndex === -1 || draggedItemIndex === targetIndex) {
        return;
      }

      const newSelectedAssetIds = [...selectedAssetIds];
      const [draggedItem] = newSelectedAssetIds.splice(draggedItemIndex, 1);
      const adjustedTargetIndex =
        draggedItemIndex < targetIndex ? targetIndex - 1 : targetIndex;

      if (draggedItem) {
        newSelectedAssetIds.splice(adjustedTargetIndex, 0, draggedItem);
        setValue("selectedAssetIds", newSelectedAssetIds, {
          shouldDirty: true,
        });
      }
    };

    const hasSelectedItems =
      (isImageListMode && selectedAssetIds.length > 0) ||
      (isThumbnailMode &&
        (selectedAssetIds.length > 0 || Boolean(selectedHexColor)));

    const SelectedItemsHeader = () => {
      if (!hasSelectedItems) {
        return null;
      }

      return (
        <div
          className={twMerge(
            "p-4 bg-surface-subtle border-b border-border",
            isThumbnailMode && "rounded-t-lg",
          )}
          style={{
            flexBasis: isThumbnailMode ? "120px" : "160px",
            flexShrink: 0,
            overflowY: "auto",
          }}
        >
          <h3 className="text-sm font-medium text-foreground-muted mb-2">
            {isThumbnailMode
              ? "Current Thumbnail"
              : `Selected Images (${selectedAssetIds.length})`}
          </h3>
          <div
            className={twMerge(
              "grid gap-2",
              isThumbnailMode
                ? "grid-cols-1 justify-start"
                : "grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6",
            )}
          >
            {isThumbnailMode && selectedHexColor && (
              <div className="flex items-center gap-2 p-1 bg-surface-default rounded border border-border-muted">
                <div
                  className="h-16 w-16 rounded border border-border-default shadow-sm"
                  style={{ backgroundColor: selectedHexColor }}
                />
                <span className="text-sm font-mono text-foreground-default">
                  {selectedHexColor}
                </span>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => {
                    setSelectedHexColor(null);
                    setValue("selectedAssetIds", [], { shouldDirty: true });
                  }}
                  aria-label="Remove color"
                  UNSAFE_className="ml-auto text-muted hover:text-danger p-1"
                >
                  <XIcon size={14} />
                </Button>
              </div>
            )}
            {selectedAssetIds.map((stagedAssetId, index) => {
              const isDraggedOver = draggedOverIndex === index;

              return (
                <div
                  key={stagedAssetId}
                  className={twMerge(
                    isThumbnailMode
                      ? "flex items-center gap-2 w-auto"
                      : "aspect-square transition-all duration-150 ease-in-out",
                    isImageListMode &&
                      isDraggedOver &&
                      "ring-2 ring-primary ring-offset-2",
                  )}
                  draggable={isImageListMode}
                  onDragStart={(e) =>
                    isImageListMode && handleDragStart(e, stagedAssetId)
                  }
                  onDragEnd={isImageListMode ? handleDragEnd : undefined}
                  onDragOver={(e) =>
                    isImageListMode && handleDragOver(e, index)
                  }
                  onDragLeave={isImageListMode ? handleDragLeave : undefined}
                  onDrop={(e) => isImageListMode && handleDrop(e, index)}
                >
                  <AssetImage
                    assetId={stagedAssetId}
                    projectId={projectId}
                    className={twMerge(
                      isThumbnailMode
                        ? "w-16 h-16 rounded border border-border-default shadow-sm"
                        : "h-full w-full pointer-events-none",
                    )}
                    onRemove={handleRemoveStagedAsset}
                    isThumbnailMode={isThumbnailMode}
                  />
                </div>
              );
            })}
            {isThumbnailMode &&
              !selectedHexColor &&
              selectedAssetIds.length === 0 && (
                <p className="text-sm text-foreground-muted col-span-full p-2 text-center">
                  (No thumbnail assigned)
                </p>
              )}
          </div>
        </div>
      );
    };

    const constructAssetUrl = (projectId: string, assetId: string): string => {
      return `https://${ASSETS_HOST}/projects/${projectId}/${assetId}`;
    };

    const AssetImage = ({
      assetId,
      projectId,
      className,
      onRemove,
      isThumbnailMode,
    }: {
      assetId: string;
      projectId: string;
      className?: string;
      onRemove: (assetId: string) => void;
      isThumbnailMode: boolean;
    }) => {
      const [isLoading, setIsLoading] = React.useState(true);
      const [hasError, setHasError] = React.useState(false);

      const assetUrl = constructAssetUrl(projectId, assetId);

      const handleImageLoad = () => {
        setIsLoading(false);
        setHasError(false);
      };

      const handleImageError = () => {
        setIsLoading(false);
        setHasError(true);
      };

      if (hasError) {
        return (
          <div
            className={twMerge(
              "bg-surface-subtle flex items-center justify-center rounded text-sm text-foreground-muted",
              className,
            )}
          >
            Failed to load
          </div>
        );
      }

      return (
        <div className="relative group">
          <div
            className={twMerge("relative overflow-hidden rounded", className)}
          >
            {isLoading && (
              <div
                className={twMerge(
                  "absolute inset-0 bg-surface-subtle animate-pulse flex items-center justify-center text-sm text-foreground-muted",
                )}
              >
                Loading...
              </div>
            )}
            <img
              src={assetUrl}
              alt="Asset"
              className={twMerge(
                "h-full w-full object-cover",
                isLoading && "opacity-0",
              )}
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
          </div>
          {isThumbnailMode && (
            <span className="text-sm text-foreground-default truncate max-w-[100px]">
              Asset {assetId.slice(0, 8)}...
            </span>
          )}
          <Button
            variant="danger"
            size="sm"
            onClick={() => onRemove(assetId)}
            UNSAFE_className={twMerge(
              isThumbnailMode
                ? "text-muted hover:text-danger p-1"
                : "absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity p-1 z-10 bg-white hover:bg-neutral-soft",
            )}
            aria-label={
              isThumbnailMode ? "Remove thumbnail image" : "Remove image"
            }
          >
            <XIcon
              size={isThumbnailMode ? 14 : 12}
              className={!isThumbnailMode ? "text-danger" : ""}
            />
          </Button>
        </div>
      );
    };

    return (
      <div className="flex flex-col h-full max-h-[85vh] overflow-hidden">
        <SelectedItemsHeader />

        <div
          className={twMerge(
            "p-0 relative bg-background-default flex flex-col flex-grow min-h-0",
            !hasSelectedItems && "rounded-t-lg",
          )}
        >
          {isThumbnailMode && (
            <div className="p-4 border-b border-border bg-background-subtle">
              <h4 className="text-xs font-medium text-foreground-muted mb-2">
                ASSIGN COLOR
              </h4>
              <SolidColorPicker
                value={selectedHexColor}
                onChange={handleSolidColorPickerChange}
                showShopStyles={false}
              />
              <p className="text-xs text-foreground-muted mt-3 text-center">
                <span className="font-semibold">&mdash; OR &mdash;</span>
              </p>
            </div>
          )}
          <div className="p-4">
            <h4 className="text-xs font-medium text-foreground-muted mb-2">
              {isThumbnailMode
                ? "ASSIGN IMAGE FROM LIBRARY"
                : "SELECT IMAGES FROM LIBRARY"}
            </h4>
          </div>
          <AssetsLibrary
            assetTypes={["image"]}
            onAssetSelected={handleAssetStaged}
            shouldAllowActions={true}
            gridColumns={3}
          />
        </div>
      </div>
    );
  },
);

ProductsOptionImageSetForm.displayName = "ProductsOptionImageSetForm";
